/**
 * Gzip storage for raw fetched pages in the `page-snapshots` bucket, shared by every runtime that
 * writes or reads it: the Supabase edge functions (Deno), the Firebase workers (Node) and the
 * dashboard (browser). It uses only the platform's own `CompressionStream` /
 * `DecompressionStream`, which all three ship, so there is one implementation and no dependency.
 *
 * Layout: new objects are gzip bytes at a `.gz` key (`…/page.html.gz`) uploaded as
 * `application/gzip`, and the `page_snapshots` row records `content_encoding = 'gzip'` with
 * `content_type` still naming the decoded body (`text/html; charset=utf-8`). The object is NOT
 * stored with an HTTP `Content-Encoding: gzip` header: a client that honours that header
 * decompresses on download and one that does not hands back gzip bytes, so a reader could not
 * know which it got. Here the bytes on the wire are always what is stored, and the row says how
 * to decode them.
 *
 * Legacy objects (written before this module) are plain text with `content_encoding` `identity`
 * or NULL. `decodePageSnapshotBody` reads both, and also gunzips any body that starts with the
 * gzip magic bytes, so a row whose encoding was never updated still decodes.
 */

export const PAGE_SNAPSHOTS_BUCKET = "page-snapshots";
/** Object content type for compressed snapshots; the bucket's allowed_mime_types includes it. */
export const GZIP_OBJECT_CONTENT_TYPE = "application/gzip";
export const GZIP_CONTENT_ENCODING = "gzip";

async function pipeBytes(
  bytes: Uint8Array,
  transform: CompressionStream | DecompressionStream,
): Promise<Uint8Array> {
  const source = new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });
  // The cast papers over DOM vs Node lib typings of the stream chunk type; both are bytes.
  const stream = source.pipeThrough(
    transform as unknown as {
      readable: ReadableStream<Uint8Array>;
      writable: WritableStream<Uint8Array>;
    },
  );
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export function gzipBytes(bytes: Uint8Array): Promise<Uint8Array> {
  return pipeBytes(bytes, new CompressionStream("gzip"));
}

export function gunzipBytes(bytes: Uint8Array): Promise<Uint8Array> {
  return pipeBytes(bytes, new DecompressionStream("gzip"));
}

/** True when the body starts with the gzip magic number (1f 8b). HTML, JSON and text never do. */
export function hasGzipMagic(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

/** Storage key for the compressed copy of `path`. */
export function gzipStoragePath(path: string): string {
  return path.endsWith(".gz") ? path : `${path}.gz`;
}

export interface EncodedPageSnapshot {
  /** Key to upload to (the requested key plus `.gz`). */
  storagePath: string;
  /** Gzip bytes to upload. */
  body: Uint8Array;
  /** Content type for the storage upload, not for the row. */
  uploadContentType: typeof GZIP_OBJECT_CONTENT_TYPE;
  /** Value for `page_snapshots.content_encoding`. */
  contentEncoding: typeof GZIP_CONTENT_ENCODING;
  /** UTF-8 byte length of the decoded body, for `page_snapshots.size_bytes`. */
  sizeBytes: number;
  /** Bytes actually stored, for `page_snapshots.metadata.storedBytes`. */
  storedBytes: number;
}

/** Compress a fetched body for upload. `storagePath` is the plain key, e.g. `…/x.html`. */
export async function encodePageSnapshot(
  content: string,
  storagePath: string,
): Promise<EncodedPageSnapshot> {
  const raw = new TextEncoder().encode(content);
  const body = await gzipBytes(raw);
  return {
    storagePath: gzipStoragePath(storagePath),
    body,
    uploadContentType: GZIP_OBJECT_CONTENT_TYPE,
    contentEncoding: GZIP_CONTENT_ENCODING,
    sizeBytes: raw.length,
    storedBytes: body.length,
  };
}

async function toBytes(body: Blob | ArrayBuffer | Uint8Array): Promise<Uint8Array> {
  // Duck-typed rather than instanceof: bodies can come from another realm (jsdom, workers).
  if (ArrayBuffer.isView(body))
    return new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
  if (typeof (body as Blob).arrayBuffer === "function") {
    return new Uint8Array(await (body as Blob).arrayBuffer());
  }
  return new Uint8Array(body as ArrayBuffer);
}

/** Raw stored bytes → decoded bytes, gunzipping when the row or the bytes say gzip. */
export async function decodePageSnapshotBytes(
  body: Blob | ArrayBuffer | Uint8Array,
  contentEncoding?: string | null,
): Promise<Uint8Array> {
  const bytes = await toBytes(body);
  return contentEncoding === GZIP_CONTENT_ENCODING || hasGzipMagic(bytes)
    ? gunzipBytes(bytes)
    : bytes;
}

/** Read a downloaded snapshot as text, whichever format it was stored in. */
export async function decodePageSnapshotBody(
  body: Blob | ArrayBuffer | Uint8Array,
  contentEncoding?: string | null,
): Promise<string> {
  return new TextDecoder().decode(await decodePageSnapshotBytes(body, contentEncoding));
}

// ---------------------------------------------------------------------------------------------
// Backfill: compress existing identity objects in bounded, resumable batches.
// ---------------------------------------------------------------------------------------------

export interface PendingPageSnapshot {
  id: string;
  storage_bucket: string;
  storage_path: string;
  content_encoding: string | null;
  metadata: Record<string, unknown> | null;
}

/** Storage + table access the backfill needs; the edge function wires it to supabase-js. */
export interface PageSnapshotBackfillStore {
  /** Rows not yet gzip in the page-snapshots bucket with id > afterId, ordered by id. */
  listPending(limit: number, afterId: string | null): Promise<PendingPageSnapshot[]>;
  /** Whole-backlog totals for the dry run (rows, stored bytes, rows whose object is missing). */
  backlog(): Promise<{ rows: number; bytes: number; missingObjects: number }>;
  /** Object bytes, or null when the object does not exist. */
  download(bucket: string, path: string): Promise<Uint8Array | null>;
  /** Upload with upsert, so a rerun after a half-finished row overwrites its own earlier copy. */
  upload(bucket: string, path: string, body: Uint8Array, contentType: string): Promise<void>;
  /**
   * Point the row at the compressed object, only if it still points at `fromPath`.
   * Returns false when no row matched (another run got there first).
   */
  markCompressed(
    id: string,
    fromPath: string,
    patch: { storage_path: string; content_encoding: string; metadata: Record<string, unknown> },
  ): Promise<boolean>;
  remove(bucket: string, path: string): Promise<void>;
}

export type BackfillOutcome =
  | "compressed"
  | "would-compress"
  | "missing-object"
  | "lost-race"
  | "old-object-not-deleted"
  | "failed";

export interface BackfillItemResult {
  id: string;
  fromPath: string;
  toPath: string;
  outcome: BackfillOutcome;
  bytesBefore?: number;
  bytesAfter?: number;
  error?: string;
}

export interface BackfillSummary {
  dryRun: boolean;
  backlog?: { rows: number; bytes: number; missingObjects: number };
  processed: number;
  compressed: number;
  bytesBefore: number;
  bytesAfter: number;
  /** Pass back as `afterId` for the next batch; null when the scan reached the end. */
  nextAfterId: string | null;
  results: BackfillItemResult[];
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

async function compressOne(
  store: PageSnapshotBackfillStore,
  row: PendingPageSnapshot,
): Promise<BackfillItemResult> {
  const fromPath = row.storage_path;
  const toPath = gzipStoragePath(fromPath);
  const result: BackfillItemResult = { id: row.id, fromPath, toPath, outcome: "failed" };
  try {
    if (toPath === fromPath) throw new Error("row is not gzip but its key already ends in .gz");
    const original = await store.download(row.storage_bucket, fromPath);
    if (!original) return { ...result, outcome: "missing-object" };
    // Never double-compress: a body that is already gzip is re-keyed as is.
    const body = hasGzipMagic(original) ? original : await gzipBytes(original);
    const decoded = await gunzipBytes(body);
    if (!hasGzipMagic(original) && !sameBytes(decoded, original)) {
      throw new Error("gzip round trip did not reproduce the original bytes");
    }
    result.bytesBefore = original.length;
    result.bytesAfter = body.length;
    await store.upload(row.storage_bucket, toPath, body, GZIP_OBJECT_CONTENT_TYPE);
    const updated = await store.markCompressed(row.id, fromPath, {
      storage_path: toPath,
      content_encoding: GZIP_CONTENT_ENCODING,
      metadata: { ...(row.metadata ?? {}), storedBytes: body.length, compressedBy: "backfill" },
    });
    // Another run moved the row: it owns deleting the old object, and our upload wrote the same
    // bytes to the same key, so there is nothing to undo.
    if (!updated) return { ...result, outcome: "lost-race" };
    try {
      await store.remove(row.storage_bucket, fromPath);
    } catch (err) {
      return { ...result, outcome: "old-object-not-deleted", error: errorMessage(err) };
    }
    return { ...result, outcome: "compressed" };
  } catch (err) {
    return { ...result, outcome: "failed", error: errorMessage(err) };
  }
}

/**
 * Compress up to `limit` legacy snapshots after `afterId`. Order per row: download, gzip,
 * verify the round trip, upload the `.gz` key, update the row, and only then delete the old
 * object. Idempotent: the pending scan skips gzip rows, the upload upserts, and the row update
 * is conditional on the old path. Resumable: pass `nextAfterId` back to continue the scan; a
 * fresh scan (afterId null) revisits rows that failed earlier.
 */
export async function compressPageSnapshotBatch(
  store: PageSnapshotBackfillStore,
  options: { limit: number; afterId?: string | null; dryRun?: boolean },
): Promise<BackfillSummary> {
  const dryRun = options.dryRun !== false;
  const rows = await store.listPending(options.limit, options.afterId ?? null);
  const summary: BackfillSummary = {
    dryRun,
    processed: rows.length,
    compressed: 0,
    bytesBefore: 0,
    bytesAfter: 0,
    nextAfterId: rows.length === options.limit ? (rows.at(-1)?.id ?? null) : null,
    results: [],
  };
  if (dryRun) {
    summary.backlog = await store.backlog();
    summary.results = rows.map((row) => ({
      id: row.id,
      fromPath: row.storage_path,
      toPath: gzipStoragePath(row.storage_path),
      outcome: "would-compress",
    }));
    return summary;
  }
  for (const row of rows) {
    const result = await compressOne(store, row);
    summary.results.push(result);
    if (result.outcome === "compressed" || result.outcome === "old-object-not-deleted") {
      summary.compressed += 1;
      summary.bytesBefore += result.bytesBefore ?? 0;
      summary.bytesAfter += result.bytesAfter ?? 0;
    }
  }
  return summary;
}
